<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end"
    tabindex="-1"
    ref="offcanvas"
    data-bs-backdrop="static"
  >
    <div class="opt-header offcanvas-header text-white py-3">
      <h5 class="offcanvas-title fw-bold mb-0 text-truncate pe-2 d-flex align-items-center gap-2">
        <i class="fa-solid fa-utensils opacity-75"></i>{{ $ln(product) }}
      </h5>
      <button
        type="button"
        class="btn-close btn-close-white shadow-none"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div ref="scrollArea" class="offcanvas-body p-0" style="-webkit-overflow-scrolling: touch;">

      <!-- Loading State -->
      <div v-if="loading" class="p-0">
        <!-- Product Info Skeleton -->
        <div class="d-flex align-items-center gap-3 p-3 border-bottom">
          <div class="flex-shrink-0 rounded-3 skeleton-shimmer" style="width: 80px; height: 80px;"/>
          <div class="flex-grow-1">
            <div class="skeleton-shimmer rounded mb-2" style="height: 20px; width: 60%;"/>
            <div class="skeleton-shimmer rounded" style="height: 22px; width: 35%;"/>
          </div>
        </div>

        <!-- Attribute Skeleton -->
        <div class="p-3 border-bottom">
          <div class="skeleton-shimmer rounded mb-3" style="height: 16px; width: 40%;"/>
          <div class="d-flex flex-wrap gap-2">
            <div class="skeleton-shimmer rounded-pill" style="height: 38px; width: 80px;" v-for="i in 3" :key="'attr-'+i"/>
          </div>
        </div>

        <!-- Modifier Skeleton -->
        <div class="p-3 border-bottom">
          <div class="skeleton-shimmer rounded mb-3" style="height: 16px; width: 50%;"/>
          <div class="d-flex flex-column gap-2">
            <div class="skeleton-shimmer rounded-3" style="height: 46px; width: 100%;" v-for="i in 3" :key="'mod-'+i"/>
          </div>
        </div>

        <!-- Remark Skeleton -->
        <div class="p-3 border-bottom">
          <div class="skeleton-shimmer rounded mb-3" style="height: 16px; width: 30%;"/>
          <div class="skeleton-shimmer rounded" style="height: 58px; width: 100%;"/>
        </div>

        <!-- Quantity Skeleton -->
        <div class="p-3 d-flex flex-column align-items-center">
          <div class="skeleton-shimmer rounded mb-3 align-self-start" style="height: 16px; width: 30%;"/>
          <div class="d-flex align-items-center gap-3">
            <div class="skeleton-shimmer rounded-circle" style="width: 40px; height: 40px;"/>
            <div class="skeleton-shimmer rounded" style="width: 40px; height: 28px;"/>
            <div class="skeleton-shimmer rounded-circle" style="width: 40px; height: 40px;"/>
          </div>
        </div>
      </div>

      <template v-else>

        <!-- Product Info — รูป/ชื่อ/ราคา/ปุ่มอ่านคำบรรยาย และเนื้อความที่กางออก
             อยู่ในบล็อกเดียวกันทั้งหมด จึงมีเส้นคั่นเส้นเดียวคือเส้นล่างของบล็อกนี้ -->
        <div class="opt-hero p-3">
          <div class="d-flex align-items-center gap-3">
            <div
              v-if="displayImage"
              class="opt-hero-img flex-shrink-0 overflow-hidden"
            >
              <img
                :src="displayImage"
                class="w-100 h-100"
                style="object-fit: cover;"
                @error="$storeIndex.onImageError"
              >
            </div>
            <div class="flex-grow-1 min-w-0">
              <h5 class="fw-bold mb-1 text-truncate">{{ $t(displayName) }}</h5>
              <div class="d-flex align-items-center flex-wrap gap-2">
                <span class="fw-bold text-theme fs-4 lh-1">{{ $storeIndex.formatThaiCurrency(basePrice) }}</span>
                <span v-if="baseCompareAtPrice" class="text-decoration-line-through text-muted small">{{ $storeIndex.formatThaiCurrency(baseCompareAtPrice) }}</span>
                <span v-if="selectedVariant && (!selectedVariant.is_in_stock || selectedVariant.is_ingredient_insufficient )" class="badge bg-dark">
                  {{ $t('Sold out') }}
                </span>
              </div>
              <button
                v-if="productDescHtml"
                type="button"
                class="desc-toggle mt-1"
                :aria-expanded="descOpen ? 'true' : 'false'"
                @click="descOpen = !descOpen"
              >
                <i class="fa-solid fa-circle-info" />{{ $t('Details') }}
                <i class="fa-solid fa-chevron-down desc-toggle__chev" :class="{ 'is-open': descOpen }" />
              </button>
            </div>
          </div>

          <!-- เนื้อความคำบรรยาย — ปิดไว้เป็นค่าเริ่มต้น ตัวเลือกจึงอยู่ตำแหน่งเดิม
               ทุกสินค้า ไม่เลื่อนตามความยาวคำบรรยาย
               v-html ได้เพราะ API sanitize มาแล้ว (Product::safeDescription
               เหลือแค่แท็กจัดข้อความ ไม่มี attribute) -->
          <div
            v-if="productDescHtml"
            v-show="descOpen"
            class="rich-text mt-2"
            v-html="productDescHtml"
          />
        </div>

        <!-- Attribute Selection -->
        <div v-for="attribute in product.attributes" :key="attribute.id" class="p-3 border-bottom">
          <h6 class="fw-bold text-dark mb-3">
            <i class="fa-solid fa-layer-group me-2 text-theme" />
            {{ $ln(attribute) }}
            <span class="text-danger">*</span>
          </h6>
          <div class="d-flex flex-wrap gap-2">
            <template
              v-for="option in attribute.options"
              :key="option.id"
            >
              <button
                v-if="isAttributeOptionAvailable(attribute.id, option.id)"
                type="button"
                class="btn rounded-pill px-3 py-2 fw-semibold"
                :class="{
                  'btn-theme text-white': attributeSelections[attribute.id] === option.id,
                  'btn-outline-secondary': attributeSelections[attribute.id] !== option.id
                }"
                @click="isAttributeOptionAvailable(attribute.id, option.id) && selectAttributeOption(attribute.id, option.id)"
              >
                {{ $ln(option) }}
              </button>
            </template>
          </div>
          <div v-for="error of v$.form.attributeSelections.$errors"
            class="d-block invalid-feedback"
            :key="error.$uid"
          >
            {{ error.$message }}
          </div>
        </div>

        <!-- Modifiers -->
        <div v-for="modifier in activeModifiers" :key="modifier.id" class="p-3 border-bottom" :class="{ 'opacity-50': isOutOfStock }">
          <h6 class="fw-bold text-dark mb-1">
            <i class="fa-solid fa-sliders me-2 text-theme" />
            {{ $ln(modifier) }}
            <span v-if="modifier.is_required" class="text-danger">*</span>
          </h6>
          <div class="text-muted small mb-3" v-if="modifier.allow_multiple && modifier.max_options > 0">
            {{ $t('Select') + ' ' + modifier.min_options + '-' + modifier.max_options + ' ' + $t('options') }}
          </div>
          <div class="text-muted small mb-3" v-else-if="!modifier.allow_multiple">
            {{ $t('Select') + ' 1 ' + $t('option') }}
          </div>

          <!-- Single select (radio) -->
          <div v-if="!modifier.allow_multiple" class="list-group list-group-flush">
            <label
              v-for="option in modifier.options"
              :key="option.id"
              class="opt-option d-flex align-items-center gap-2 rounded-3 mb-2 border cursor-pointer"
              :class="{
                'opt-option--selected': modifierSelections[modifier.id] === option.id,
                'opacity-50': !option.is_in_stock || option.is_ingredient_insufficient
              }"
            >
              <input
                type="radio"
                class="form-check-input mt-0 flex-shrink-0"
                :name="'modifier-' + modifier.id"
                :value="option.id"
                v-model="modifierSelections[modifier.id]"
                :disabled="isOutOfStock || !option.is_in_stock || option.is_ingredient_insufficient"
              >
              <div class="flex-grow-1" style="font-size: 0.9rem;">
                {{ $ln(option) }}
                <span v-if="!option.is_in_stock || option.is_ingredient_insufficient" class="badge bg-dark ms-1" style="font-size: 0.65rem;">{{ $t('Sold out') }}</span>
              </div>
              <span v-if="parseFloat(option.price_adjustment) !== 0" class="opt-price-adj small fw-semibold">
                +{{ $storeIndex.formatThaiCurrency(option.price_adjustment) }}
              </span>
            </label>
          </div>

          <!-- Multi select (checkbox) -->
          <div v-else class="list-group list-group-flush">
            <label
              v-for="option in modifier.options"
              :key="option.id"
              class="opt-option d-flex align-items-center gap-2 rounded-3 mb-2 border cursor-pointer"
              :class="{
                'opt-option--selected': (modifierSelections[modifier.id] || []).includes(option.id),
                'opacity-50': !option.is_in_stock || option.is_ingredient_insufficient
              }"
            >
              <input
                type="checkbox"
                class="form-check-input mt-0 flex-shrink-0"
                :value="option.id"
                :checked="(modifierSelections[modifier.id] || []).includes(option.id)"
                @change="toggleMultiOption(modifier, option.id)"
                :disabled="
                  isOutOfStock
                  || !option.is_in_stock
                  || option.is_ingredient_insufficient
                  || (modifier.max_options > 0
                  && (modifierSelections[modifier.id] || []).length >= modifier.max_options
                  && !(modifierSelections[modifier.id] || []).includes(option.id))
                "
              >
              <div class="flex-grow-1" style="font-size: 0.9rem;">
                {{ $ln(option) }}
                <span v-if="!option.is_in_stock || option.is_ingredient_insufficient" class="badge bg-dark ms-1" style="font-size: 0.65rem;">{{ $t('Sold out') }}</span>
              </div>
              <span v-if="parseFloat(option.price_adjustment) !== 0" class="opt-price-adj small fw-semibold">
                +{{ $storeIndex.formatThaiCurrency(option.price_adjustment) }}
              </span>
            </label>
          </div>
        </div>

        <!-- Remark -->
        <div class="p-3 border-bottom">
          <h6 class="fw-bold text-dark mb-3">
            <i class="fa-solid fa-comment-dots me-2 text-theme" />
            {{ $t('Remark') }}
          </h6>
          <textarea
            v-model="form.remark"
            class="form-control shadow-none"
            rows="2"
            :placeholder="$t('e.g. No spicy, extra sauce')+'...'"
            style="font-size: 0.9rem; resize: none;"
            :disabled="isOutOfStock"
          ></textarea>
        </div>

        <!-- Quantity -->
        <div class="p-3">
          <h6 class="fw-bold text-dark mb-3">
            <i class="fa-solid fa-hashtag me-2 text-theme" />
            {{ $t('Quantity') }}
          </h6>
          <div class="opt-qty d-flex align-items-center justify-content-center gap-3 mx-auto">
            <button
              class="opt-qty-btn d-flex align-items-center justify-content-center"
              @click="!isOutOfStock && form.quantity > 1 && form.quantity--"
              :disabled="isOutOfStock || form.quantity <= 1"
            >
              <i class="fa-solid fa-minus"></i>
            </button>
            <span class="fw-bold fs-3 text-dark" style="min-width: 2.5rem; text-align: center;">{{ form.quantity }}</span>
            <button
              class="opt-qty-btn d-flex align-items-center justify-content-center"
              @click="!isOutOfStock && form.quantity++"
              :disabled="isOutOfStock"
            >
              <i class="fa-solid fa-plus"></i>
            </button>
          </div>
          <div v-for="error of v$.form.quantity.$errors"
            class="d-block invalid-feedback text-center"
            :key="error.$uid"
          >
            {{ error.$message }}
          </div>
        </div>

      </template>
    </div>

    <!-- Footer -->
    <div class="opt-footer p-3">
      <button
        class="btn btn-theme opt-add-btn w-100 py-3 fw-bold text-white d-flex align-items-center justify-content-between gap-2"
        :disabled="loading || !canAdd"
        @click="handleAdd"
      >
        <span class="d-inline-flex align-items-center gap-2">
          <i :class="editData ? 'fa-solid fa-check' : 'fa-solid fa-cart-plus'" />
          {{ editData ? $t('Update Cart') : $t('Add to Cart') }}
        </span>
        <span class="opt-add-price">{{ $storeIndex.formatThaiCurrency(totalPrice) }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { Sessions } from '@/services/endpoint/pos.service';
import { isServiceSessionInvalid } from '@/services/endpoint/services.service';
import { useVuelidate } from '@vuelidate/core';
import { useScrollRestore } from '@/composables/useScrollRestore';
import { pickLocalized } from '@/utils/i18nName';
import validationProductOptions from './assets/template/validation-product-options';

export default {
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: {
    $lazy: true,
  },
  validations() {
    return validationProductOptions(this.form, this.product, this.activeModifiers);
  },
  props: {
    id: {
      type: Number,
      default: 0
    },
    editData: {
      type: Object,
      default: null
    },
    editIndex: {
      type: Number,
      default: -1
    },
    // map {product_id: ราคา platform} — session delivery (external) เท่านั้น
    // ทับราคาสินค้า/variant + ปิดโปรในร้าน (docs/delivery-platform-pricing.md)
    platformPrices: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: true,
      descOpen: false,   // คำบรรยายยาว: ปิดไว้ก่อน พนักงานที่ต้องดูค่อยกดเปิด
      product: {
        name: '',
        price: 0,
        image_url: null,
        thumbnail_url: null,
        attributes: [],
        variants: [],
        modifiers: []
      },
      form: {
        attributeSelections: {},
        modifierSelections: {},
        quantity: 1,
        remark: ''
      }
    };
  },
  computed: {
    attributeSelections: {
      get() { return this.form.attributeSelections; },
      set(val) { this.form.attributeSelections = val; }
    },
    modifierSelections: {
      get() { return this.form.modifierSelections; },
      set(val) { this.form.modifierSelections = val; }
    },
    selectedVariant() {
      if (!this.product.variants.length || !this.product.attributes.length) return null;

      const selectedOptionIds = Object.values(this.attributeSelections);
      if (selectedOptionIds.length !== this.product.attributes.length) return null;

      return this.product.variants.find(v => {
        if (!v.option_ids || v.option_ids.length !== selectedOptionIds.length) return false;
        return selectedOptionIds.every(id => v.option_ids.includes(id));
      }) || null;
    },
    displayName() {
      if (this.selectedVariant) return this.selectedVariant.name;
      return this.product.name;
    },
    displayImage() {
      if (this.selectedVariant) {
        return this.selectedVariant.image_url || this.selectedVariant.thumbnail_url || this.product.image_url || this.product.thumbnail_url;
      }
      return this.product.image_url || this.product.thumbnail_url;
    },
    /**
     * รายละเอียดสินค้า (HTML ที่ API sanitize มาแล้ว) — variant ที่เลือกมีคำบรรยาย
     * ของตัวเองก็ใช้ตัวนั้น
     * ใช้ pickLocalized ตรง ๆ (ไม่ผ่าน $ln) เพราะ $ln ส่งค่าเข้า $t อีกชั้น ซึ่งเหมาะกับ
     * "ชื่อ" ที่อาจมีคีย์ในพจนานุกรม แต่ประโยคบรรยายไม่มีทางมีคีย์อยู่แล้ว
     */
    productDescHtml() {
      const source = this.selectedVariant && this.selectedVariant.description
        ? this.selectedVariant
        : this.product;
      return pickLocalized(source, this.$i18n.locale, 'description');
    },
    activeModifiers() {
      if (this.selectedVariant) {
        return this.selectedVariant.modifiers || [];
      }
      return this.product.modifiers || [];
    },
    // ราคา platform ของ variant/สินค้าแม่ที่เลือกอยู่ — null = ไม่มี (ใช้ราคาหน้าร้าน)
    platformOverridePrice() {
      if (!this.platformPrices) return null;
      const src = this.selectedVariant ? this.selectedVariant : this.product;
      const override = this.platformPrices[src?.id];
      return (override !== undefined && override !== null) ? parseFloat(override) : null;
    },
    // item_discount: ราคาหลังลด (BE คำนวณ) ของ variant/สินค้าแม่ — null = ไม่มีโปร
    // ออเดอร์ platform: โปรในร้านไม่ทำงาน (BE ข้าม) → ห้ามโชว์ราคาโปร
    basePromotionPrice() {
      if (this.platformOverridePrice != null) return null;
      const src = this.selectedVariant ? this.selectedVariant : this.product;
      const pp = src?.promotion_price;
      return (pp !== null && pp !== undefined && pp !== '') ? parseFloat(pp) : null;
    },
    baseOriginalPrice() {
      if (this.platformOverridePrice != null) return this.platformOverridePrice;
      const src = this.selectedVariant ? this.selectedVariant : this.product;
      return parseFloat(src?.price) || 0;
    },
    basePrice() {
      return this.basePromotionPrice != null ? this.basePromotionPrice : this.baseOriginalPrice;
    },
    // ราคาเต็ม (ขีดฆ่า) — โปรมาก่อน (โชว์ราคาปกติ) ไม่งั้นใช้ compare_at_price ถ้าสูงกว่าจริง
    baseCompareAtPrice() {
      if (this.basePromotionPrice != null) return this.baseOriginalPrice;
      const src = this.selectedVariant ? this.selectedVariant : this.product;
      const compare = Number(src?.compare_at_price);
      return !isNaN(compare) && compare > this.basePrice ? compare : 0;
    },
    modifierPriceAdjustment() {
      let adj = 0;
      for (const modifier of this.activeModifiers) {
        const sel = this.modifierSelections[modifier.id];
        if (!sel) continue;

        if (modifier.allow_multiple && Array.isArray(sel)) {
          for (const optId of sel) {
            const opt = modifier.options.find(o => o.id === optId);
            if (opt) adj += parseFloat(opt.price_adjustment) || 0;
          }
        } else {
          const opt = modifier.options.find(o => o.id === sel);
          if (opt) adj += parseFloat(opt.price_adjustment) || 0;
        }
      }
      return adj;
    },
    totalPrice() {
      return (this.basePrice + this.modifierPriceAdjustment) * this.form.quantity;
    },
    isOutOfStock() {
      if (this.product.attributes.length > 0 && this.selectedVariant) {
        return !this.selectedVariant.is_in_stock || this.selectedVariant.is_ingredient_insufficient;
      }
      return false;
    },
    canAdd() {
      if (this.product.attributes.length > 0) {
        if (!this.selectedVariant) return false;
        if (!this.selectedVariant.is_in_stock) return false;
        if (this.selectedVariant.is_ingredient_insufficient) return false;
      }

      for (const modifier of this.activeModifiers) {
        if (!modifier.is_required) continue;
        const sel = this.modifierSelections[modifier.id];

        if (modifier.allow_multiple) {
          if (!sel || !sel.length) return false;
          if (modifier.min_options > 0 && sel.length < modifier.min_options) return false;
        } else {
          if (!sel) return false;
        }
      }
      return true;
    }
  },
  watch: {
    selectedVariant(newVal, oldVal) {
      if (newVal?.id !== oldVal?.id) {
        this.modifierSelections = {};
        if (!this.editData) {
          this.initDefaultModifiers();
        }
      }
    }
  },
  methods: {
    isAttributeOptionAvailable(attributeId, optionId) {
      const testSelections = { ...this.attributeSelections, [attributeId]: optionId };
      const selectedOptionIds = Object.values(testSelections);

      return this.product.variants.some(v => {
        return selectedOptionIds.every(id => v.option_ids.includes(id));
      });
    },
    selectAttributeOption(attributeId, optionId) {
      this.attributeSelections = { ...this.attributeSelections, [attributeId]: optionId };
    },
    toggleMultiOption(modifier, optionId) {
      const current = this.modifierSelections[modifier.id] || [];
      const idx = current.indexOf(optionId);

      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        if (modifier.max_options > 0 && current.length >= modifier.max_options) return;
        current.push(optionId);
      }

      this.modifierSelections[modifier.id] = [...current];
    },
    handleAdd() {
      this.v$.form.$touch();
      //if (this.v$.form.$invalid) return;
      if (!this.canAdd) return;

      const selectedMods = [];
      for (const modifier of this.activeModifiers) {
        const sel = this.modifierSelections[modifier.id];
        if (!sel || (Array.isArray(sel) && sel.length === 0)) continue;

        const optionIds = Array.isArray(sel) ? sel : [sel];
        const options = modifier.options.filter(o => optionIds.includes(o.id));

        selectedMods.push({
          modifier_id: modifier.id,
          modifier_name: modifier.name,
          modifier_name_en: modifier.name_en,
          options: options.map(o => ({
            id: o.id,
            name: o.name,
            name_en: o.name_en,
            price_adjustment: parseFloat(o.price_adjustment) || 0
          }))
        });
      }

      const cartData = {
        product_id: this.product.id,
        variant_id: this.selectedVariant?.id || null,
        name: this.product.name,
        name_en: this.product.name_en,
        variant_name: this.selectedVariant?.name || null,
        variant_name_en: this.selectedVariant?.name_en || null,
        unit_price: this.basePrice + this.modifierPriceAdjustment,
        quantity: this.form.quantity,
        remark: this.form.remark.trim() || null,
        selected_modifiers: selectedMods
      };

      if (this.editData) {
        this.$emit('updateCartItem', { index: this.editIndex, ...cartData });
      } else {
        this.$emit('addToCart', cartData);
      }

      $(this.$refs.offcanvas).offcanvas('hide');
    },
    async getInfo() {
      this.loading = true;
      await Sessions.productInfo(this.$route.params.token, this.id).then(res => {
        this.product = res.data.data;
        if (this.editData) {
          this.prefillFromEditData();
        } else {
          this.initDefaultAttributes();
          this.initDefaultModifiers();
        }
        this.loading = false;
      }).catch(err => {
        // Session gone (token closed/moved/stale) → close the sheet and send
        // staff back to the sessions list instead of a dead options sheet.
        if (isServiceSessionInvalid(err)) {
          $(this.$refs.offcanvas).offcanvas('hide');
          this.$router.replace({ name: 'pos.sessions' });
          return;
        }
			  throw err;
      });
    },
    prefillFromEditData() {
      if (!this.editData) return;

      this.form.quantity = this.editData.quantity || 1;
      this.form.remark = this.editData.remark || '';

      // Pre-fill attribute selections from variant id, falling back to attribute_options list (for sent items)
      if (this.product.variants.length > 0) {
        let variant = null;
        if (this.editData._variant_id) {
          variant = this.product.variants.find(v => v.id === this.editData._variant_id);
        }
        if (!variant && Array.isArray(this.editData._attribute_options) && this.editData._attribute_options.length > 0) {
          const optionIds = this.editData._attribute_options;
          variant = this.product.variants.find(v => {
            return Array.isArray(v.option_ids)
              && v.option_ids.length === optionIds.length
              && optionIds.every(id => v.option_ids.includes(id));
          });
        }
        if (variant && variant.option_ids) {
          const selections = {};
          for (const attr of this.product.attributes) {
            const matchedOption = attr.options.find(o => variant.option_ids.includes(o.id));
            if (matchedOption) {
              selections[attr.id] = matchedOption.id;
            }
          }
          this.attributeSelections = selections;
        }
      }

      // Pre-fill modifier selections after watcher has settled
      this.$nextTick(() => {
        // Sent items pass a flat list of modifier_option_ids; cart items pass _selected_modifiers
        if (Array.isArray(this.editData._modifier_option_ids) && this.editData._modifier_option_ids.length > 0) {
          const modSel = {};
          const optionIds = this.editData._modifier_option_ids;
          for (const modifier of this.activeModifiers) {
            const matched = modifier.options.filter(o => optionIds.includes(o.id));
            if (matched.length === 0) continue;
            if (modifier.allow_multiple) {
              modSel[modifier.id] = matched
                .filter(o => o.is_in_stock && !o.is_ingredient_insufficient)
                .map(o => o.id);
            } else {
              const opt = matched[0];
              modSel[modifier.id] = (!opt.is_in_stock || opt.is_ingredient_insufficient) ? null : opt.id;
            }
          }
          this.modifierSelections = modSel;
          return;
        }

        if (this.editData._selected_modifiers && this.editData._selected_modifiers.length > 0) {
          const modSel = {};
          for (const mod of this.editData._selected_modifiers) {
            const productMod = this.activeModifiers.find(m => m.id === mod.modifier_id);
            if (productMod) {
              if (productMod.allow_multiple) {
                modSel[mod.modifier_id] = mod.options
                  .filter(o => {
                    const productOpt = productMod.options.find(po => po.id === o.id);
                    return productOpt ? productOpt.is_in_stock && !productOpt.is_ingredient_insufficient : true;
                  })
                  .map(o => o.id);
              } else {
                const firstOpt = mod.options[0];
                if (firstOpt) {
                  const productOpt = productMod.options.find(po => po.id === firstOpt.id);
                  modSel[mod.modifier_id] = (productOpt && (!productOpt.is_in_stock || productOpt.is_ingredient_insufficient)) ? null : firstOpt.id;
                }
              }
            }
          }
          this.modifierSelections = modSel;
        }
      });
    },
    initDefaultModifiers() {
      this.$nextTick(() => {
        const modSel = {};
        for (const modifier of this.activeModifiers) {
          const defaults = modifier.options.filter(o => o.is_default && (o.is_in_stock && !o.is_ingredient_insufficient));
          if (!defaults.length) continue;

          if (modifier.allow_multiple) {
            modSel[modifier.id] = defaults.map(o => o.id);
          } else {
            modSel[modifier.id] = defaults[0].id;
          }
        }
        this.modifierSelections = modSel;
      });
    },
    initDefaultAttributes() {
      if (this.product.attributes.length > 0 && this.product.variants.length > 0) {
        const firstInStock = this.product.variants.find(v => v.is_in_stock && v.is_ingredient_insufficient) || this.product.variants[0];
        if (firstInStock && firstInStock.option_ids) {
          const selections = {};
          for (const attr of this.product.attributes) {
            const matchedOption = attr.options.find(o => firstInStock.option_ids.includes(o.id));
            if (matchedOption) {
              selections[attr.id] = matchedOption.id;
            }
          }
          this.attributeSelections = selections;
        }
      }
    }
  },
  mounted() {
    $(this.$refs.offcanvas).offcanvas('show');

    this.getInfo();

    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$emit('close');
    });

    useScrollRestore(() => this.$refs.scrollArea);
  }
}
</script>

<style scoped>
/* Product options sheet — themed to match the cart panel. Brand orange
   #e96c15 / #d46010. */

.min-w-0 { min-width: 0; }

/* Header — orange gradient (same as the cart panel header). */
.opt-header {
  background: linear-gradient(135deg, #e96c15 0%, #d46010 100%);
  border: none;
}
.opt-header .offcanvas-title { font-size: 1.05rem; }

/* Hero: product image + name + price on a soft orange tint. */
.opt-hero {
  background: rgba(233, 108, 21, 0.05);
  border-bottom: 1px solid #f0e2d8;
}
/* ปุ่มอ่านคำบรรยาย — ลิงก์เล็ก ๆ ใต้ชื่อ/ราคาใน hero ไม่ใช่กล่อง เพื่อไม่ให้แย่ง
   สายตาไปจากตัวเลือกซึ่งเป็นงานหลักของแผ่นนี้ */
.desc-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0;
  border: none;
  background: transparent;
  color: #d46010;
  font-size: 0.78rem;
  font-weight: 600;
}
.desc-toggle:focus,
.desc-toggle:focus-visible { outline: none; box-shadow: none; }
.desc-toggle__chev {
  font-size: 0.65rem;
  transition: transform 0.2s ease;
}
.desc-toggle__chev.is-open { transform: rotate(180deg); }
/* เนื้อหาที่มาจาก editor หลังบ้าน (ย่อหน้า/ตัวเน้น/ลิสต์เก่า) — คุมระยะให้พอดี
   กับการ์ด ไม่ใช้ margin ก้อนใหญ่แบบ default ของเบราว์เซอร์
   line-height 1.85 เผื่อวรรณยุกต์/สระไทยไม่ให้บรรทัดแน่นเกินไป */
.rich-text {
  color: #5f6673;
  font-size: 0.875rem;
  line-height: 1.85;
}
.rich-text :deep(p) { margin-bottom: 0.35rem; }
.rich-text :deep(p:last-child) { margin-bottom: 0; }
.rich-text :deep(ul),
.rich-text :deep(ol) { margin-bottom: 0.35rem; padding-left: 1.15rem; }
.rich-text :deep(ul:last-child),
.rich-text :deep(ol:last-child) { margin-bottom: 0; }
.rich-text :deep(li) { margin-bottom: 0.1rem; }
.rich-text :deep(strong),
.rich-text :deep(b) { color: #343a40; font-weight: 700; }
.rich-text :deep(u) { text-decoration-color: rgba(233, 108, 21, 0.6); }
.opt-hero-img {
  width: 84px;
  height: 84px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #ececec;
}

/* Selectable modifier rows. */
.opt-option {
  padding: 0.65rem 0.8rem;
  background: #fff;
  border-color: #e7e7e7 !important;
  font-size: 0.9rem;
  transition: border-color 0.15s ease, background-color 0.15s ease,
              box-shadow 0.15s ease;
}
.opt-option--selected {
  border-color: #e96c15 !important;
  background: rgba(233, 108, 21, 0.08);
  box-shadow: 0 2px 8px rgba(233, 108, 21, 0.12);
}
.opt-price-adj { color: #d46010; }

/* Radio/checkbox → brand orange when checked (Bootstrap default is blue). */
.opt-option .form-check-input:checked {
  background-color: #e96c15;
  border-color: #e96c15;
}
.opt-option .form-check-input:focus {
  border-color: #f0a368;
  box-shadow: 0 0 0 0.2rem rgba(233, 108, 21, 0.25);
}

/* Quantity stepper. */
.opt-qty-btn {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1.5px solid #e96c15;
  background: #fff;
  color: #d46010;
  font-size: 0.95rem;
  transition: background-color 0.15s ease, color 0.15s ease, transform 0.1s ease;
}
.opt-qty-btn:hover:not(:disabled) { background: #e96c15; color: #fff; }
.opt-qty-btn:active:not(:disabled) { transform: scale(0.92); }
.opt-qty-btn:disabled {
  border-color: #e3e3e3;
  color: #c2c2c2;
  cursor: not-allowed;
}

/* Footer + add button. */
.opt-footer {
  background: #fff;
  border-top: 1px solid #ececec;
  box-shadow: 0 -4px 14px rgba(0, 0, 0, 0.04);
}
.opt-add-btn {
  border-radius: 14px;
  font-size: 1rem;
}
.opt-add-price {
  background: rgba(255, 255, 255, 0.22);
  padding: 0.2rem 0.7rem;
  border-radius: 999px;
  font-size: 0.95rem;
}
</style>


